import { useEffect, type FC, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@hooks/useLanguage';
import { useAuthInitialize } from '@/hooks/useAuth';
import { PageLoadingSpinner } from '@/components/common/LoadingSpinner';
import { ProtectedRoute, RoleRoute, GuestRoute } from '@/components/auth';
import {
  ErrorBoundary,
  PageErrorFallback,
  NotFoundPage,
} from '@/components/common/ErrorBoundary';
import { lazyWithRetry } from '@/utils/lazyWithRetry';

/**
 * Lazy-loaded page components for code splitting
 */
const LoginPage = lazyWithRetry(() => import('@/pages/auth/LoginPage'));
const ChangePasswordPage = lazyWithRetry(() => import('@/pages/auth/ChangePasswordPage'));
const ForgotPasswordPage = lazyWithRetry(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazyWithRetry(() => import('@/pages/auth/ResetPasswordPage'));
const SetPasswordPage = lazyWithRetry(() => import('@/pages/auth/SetPasswordPage'));

// Public Pages (no authentication required)
const PlayPage = lazyWithRetry(() => import('@/pages/public/PlayPage'));

// Public Layout for audio pages
const PublicLayout = lazyWithRetry(() => import('@/components/layout/PublicLayout'));

// Admin Layout (lazy loaded as it includes significant UI)
const AdminLayout = lazyWithRetry(() => import('@/components/layout/AdminLayout'));

// Student Layout (lazy loaded for student portal)
const StudentLayout = lazyWithRetry(() => import('@/components/layout/StudentLayout'));

// Admin Dashboard (lazy loaded)
const AdminDashboardPage = lazyWithRetry(() => import('@/pages/admin/dashboard'));

// Admin Pages (lazy loaded for code splitting)
const ContentDashboard = lazyWithRetry(() => import('@/pages/admin/content/ContentDashboard'));
const LevelManagement = lazyWithRetry(() => import('@/pages/admin/content/LevelManagement'));
const BookList = lazyWithRetry(() => import('@/pages/admin/content/BookList'));
const TeacherGuideList = lazyWithRetry(() => import('@/pages/admin/content/TeacherGuideList'));
const BookForm = lazyWithRetry(() => import('@/pages/admin/content/BookForm'));
const BookPdfUpload = lazyWithRetry(() => import('@/pages/admin/content/BookPdfUpload'));
const PageManagement = lazyWithRetry(() => import('@/pages/admin/content/PageManagement'));
const UnitManagement = lazyWithRetry(() => import('@/pages/admin/content/UnitManagement'));
const BookVideos = lazyWithRetry(() => import('@/pages/admin/content/BookVideos'));
const LessonManagement = lazyWithRetry(() => import('@/pages/admin/content/LessonManagement'));
const SessionManagement = lazyWithRetry(() => import('@/pages/admin/content/SessionManagement'));
const LessonPlanEditor = lazyWithRetry(() => import('@/pages/admin/content/LessonPlanEditor'));
const ReviewQueue = lazyWithRetry(() => import('@/pages/admin/content/ReviewQueue'));
const ReviewMode = lazyWithRetry(() => import('@/pages/admin/content/ReviewMode'));
const AdminDecksPage = lazyWithRetry(() => import('@/pages/admin/content/AdminDecksPage'));
const StructureImport = lazyWithRetry(() => import('@/pages/admin/content/StructureImport'));
const ContentSetup = lazyWithRetry(() => import('@/pages/admin/content/Setup'));
const TeacherGuideUpload = lazyWithRetry(() => import('@/pages/admin/content/TeacherGuideUpload'));
const AudioManagement = lazyWithRetry(() => import('@/pages/admin/content/AudioManagement'));
const VideoManagement = lazyWithRetry(() => import('@/pages/admin/content/VideoManagement'));
const QRCodeList = lazyWithRetry(() => import('@/pages/admin/content/QRCodeList'));
const QRCodeManagement = lazyWithRetry(() => import('@/pages/admin/content/QRCodeManagement'));
const TierManagement = lazyWithRetry(() => import('@/pages/admin/tiers'));

// Admin User Management Pages
const UserList = lazyWithRetry(() => import('@/pages/admin/users'));
const UserForm = lazyWithRetry(() => import('@/pages/admin/users/UserForm'));
const UserDetail = lazyWithRetry(() => import('@/pages/admin/users/UserDetail'));

// Admin School Management Pages
const SchoolList = lazyWithRetry(() => import('@/pages/admin/schools'));
const SchoolFormPage = lazyWithRetry(() => import('@/pages/admin/schools/SchoolFormPage'));
const SchoolDetailPage = lazyWithRetry(() => import('@/pages/admin/schools/SchoolDetailPage'));

// Student Portal Pages (student-specific)
const StudentHomePage = lazyWithRetry(() => import('@/pages/student/StudentHomePage'));
const StudentProfilePage = lazyWithRetry(() => import('@/pages/student/StudentProfilePage'));
const StudentGamesPage = lazyWithRetry(() => import('@/pages/student/games/StudentGamesPage'));
const GamePlayPage = lazyWithRetry(() => import('@/pages/student/games/GamePlayPage'));
const GameHistoryPage = lazyWithRetry(() => import('@/pages/student/games/GameHistoryPage'));
const GameLeaderboardPage = lazyWithRetry(() => import('@/pages/student/games/GameLeaderboardPage'));

// Teacher Portal Layout
const TeacherLayout = lazyWithRetry(() => import('@/components/layout/TeacherLayout'));

// Teacher Portal Pages (teacher-specific)
// Level-first navigation (Phase 24): levels grid home + per-level hub.
// Phase 25 merged the older type-first tile home (TeacherHomePage) INTO the
// levels home, so /teacher/browse now redirects to /teacher. TeacherHomePage
// stays on disk (it owns `getTeacherGreetingKey`) but is no longer routed.
const TeacherLevelsHomePage = lazyWithRetry(() => import('@/pages/teacher/TeacherLevelsHomePage'));
const TeacherLevelHubPage = lazyWithRetry(() => import('@/pages/teacher/TeacherLevelHubPage'));
const LevelVideosPage = lazyWithRetry(() => import('@/pages/teacher/LevelVideosPage'));
const TeacherLevelDecksPage = lazyWithRetry(() => import('@/pages/teacher/TeacherLevelDecksPage'));
const TeacherProfilePage = lazyWithRetry(() => import('@/pages/teacher/TeacherProfilePage'));
const TeacherGuidesBrowsePage = lazyWithRetry(() => import('@/pages/teacher/TeacherGuidesBrowsePage'));
const TeacherGuideViewerPage = lazyWithRetry(() => import('@/pages/teacher/TeacherGuideViewerPage'));
const TeacherGamesPage = lazyWithRetry(() => import('@/pages/teacher/TeacherGamesPage'));
const CreateGamePage = lazyWithRetry(() => import('@/pages/teacher/CreateGamePage'));
const TeacherLessonPlansPage = lazyWithRetry(() => import('@/pages/teacher/TeacherLessonPlansPage'));
const TeacherLessonPlanPreferencesPage = lazyWithRetry(() => import('@/pages/teacher/TeacherLessonPlanPreferencesPage'));
// Shared Content Pages (used by Student, Teacher, Admin portals)
const BooksBrowsePage = lazyWithRetry(() => import('@/pages/content/BooksBrowsePage'));
const BookViewerPage = lazyWithRetry(() => import('@/pages/content/BookViewerPage'));
const AudioBrowsePage = lazyWithRetry(() => import('@/pages/content/AudioBrowsePage'));
const AudioListPage = lazyWithRetry(() => import('@/pages/content/AudioListPage'));
const VideosBrowsePage = lazyWithRetry(() => import('@/pages/content/VideosBrowsePage'));

/**
 * Public Audio Layout Wrapper
 *
 * Wrapper component for public audio pages that provides
 * the audio-specific layout with MiniPlayer support.
 */
const PublicAudioLayoutWrapper: FC = () => {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <PageLoadingSpinner variant="primary" />
        </div>
      }
    >
      <PublicLayout variant="audio" showMiniPlayer={true} showFooter={true} />
    </Suspense>
  );
};

/**
 * Route-Level Error Boundary Wrapper
 *
 * Wraps the main Routes with an ErrorBoundary that auto-resets
 * when the route changes. This ensures errors in one route
 * don't persist when navigating to another route.
 */
const RouteErrorBoundaryWrapper: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation();

  return (
    <ErrorBoundary
      FallbackComponent={PageErrorFallback}
      resetKeys={[location.pathname]}
      onError={(error, errorInfo) => {
        // Log error for monitoring (could send to error reporting service)
        console.error('Route Error:', error, errorInfo);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

/**
 * Root Application Component
 *
 * Manages document direction and language based on app state.
 * Provides routing structure with layout wrappers and auth guards.
 *
 * Route Structure:
 * - Root: redirects to dashboard (if authenticated) or login (if not)
 * - Guest routes: /login (redirects authenticated users)
 * - Protected routes: /admin/*, /teacher/*, /student/*
 */
const App: FC = () => {
  const { locale, direction, t } = useLanguage();
  const initialize = useAuthInitialize();

  // Initialize auth state on app load
  useEffect(() => {
    initialize();
  }, [initialize]);

  // Sync document direction and language with app state
  useEffect(() => {
    document.documentElement.dir = direction;
    document.documentElement.lang = locale;
  }, [direction, locale]);

  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <PageLoadingSpinner variant="primary" label={t('common.loading')} />
        </div>
      }
    >
      <RouteErrorBoundaryWrapper>
        <Routes>

          {/* ========================================
              PUBLIC AUDIO ROUTES
              Accessible without authentication
              Uses simplified audio layout with MiniPlayer
          ======================================== */}
          <Route element={<PublicAudioLayoutWrapper />}>
            <Route path="/audio" element={<AudioBrowsePage />} />
            <Route path="/audio/:bookId" element={<AudioListPage />} />
          </Route>

          {/* ========================================
              OTHER PUBLIC ROUTES
              Accessible without authentication
          ======================================== */}
          <Route path="/play/:code" element={<PlayPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          {/* Token rides in the URL fragment (#token=<raw>), not the path,
              so the single-use secret never lands in CDN/proxy access logs
              or browser history (dim-token-url-01). */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite" element={<SetPasswordPage />} />

          {/* ========================================
              GUEST ROUTES
              Only for unauthenticated users
              Redirects to role dashboard if logged in
              Both / and /login render LoginPage
          ======================================== */}
          <Route element={<GuestRoute />}>
            <Route path="/" element={<LoginPage />} />
            <Route path="/login" element={<LoginPage />} />
          </Route>

          {/* ========================================
              CHANGE PASSWORD ROUTE
              For authenticated users who must change their password
              Accessible only when forcePasswordChange is true
          ======================================== */}
          <Route element={<ProtectedRoute />}>
            <Route path="/auth/change-password" element={<ChangePasswordPage />} />
          </Route>

          {/* ========================================
              ADMIN ROUTES
              Requires authentication + ADMIN role
          ======================================== */}
          <Route element={<ProtectedRoute />}>
            <Route element={<RoleRoute roles="ADMIN" />}>
              <Route element={<AdminLayout />}>
                <Route path="/admin" element={<AdminDashboardPage />} />
                <Route path="/admin/schools" element={<SchoolList />} />
                <Route path="/admin/schools/new" element={<SchoolFormPage />} />
                <Route path="/admin/schools/:id" element={<SchoolFormPage />} />
                <Route path="/admin/schools/:id/view" element={<SchoolDetailPage />} />
                <Route path="/admin/users" element={<UserList />} />
                <Route path="/admin/users/new" element={<UserForm />} />
                <Route path="/admin/users/:id" element={<UserForm />} />
                <Route path="/admin/users/:id/view" element={<UserDetail />} />
                <Route path="/admin/content" element={<ContentDashboard />} />
                <Route path="/admin/content/levels" element={<LevelManagement />} />
                <Route path="/admin/content/books" element={<BookList />} />
                <Route path="/admin/content/books/new" element={<BookForm />} />
                <Route path="/admin/content/books/:id" element={<BookForm />} />
                <Route path="/admin/content/books/:id/pdf" element={<BookPdfUpload />} />
                <Route path="/admin/content/books/:bookId/pages" element={<PageManagement />} />
                <Route path="/admin/content/books/:bookId/units" element={<UnitManagement />} />
                {/* Phase 26 Slice 2: book-level Videos overview, organized Unit -> exact Page */}
                <Route path="/admin/content/books/:bookId/videos" element={<BookVideos />} />
                {/* Lesson-Plans platform (Phase 23): Unit → Lesson → Session → Plan */}
                <Route path="/admin/content/units/:unitId/lessons" element={<LessonManagement />} />
                <Route path="/admin/content/lessons/:lessonId/sessions" element={<SessionManagement />} />
                <Route path="/admin/content/sessions/:sessionId/plan" element={<LessonPlanEditor />} />
                {/* Phase 26 Slice 3: Review Queue (level-scoped list) + full-screen Review Mode */}
                <Route path="/admin/content/lesson-plans/review" element={<ReviewQueue />} />
                <Route path="/admin/content/lesson-plans/review/:levelId" element={<ReviewMode />} />
                <Route path="/admin/content/lesson-plans/import" element={<StructureImport />} />
                {/* Phase 27 Slice D: level-wide PowerPoints screen — one click from the sidebar */}
                <Route path="/admin/content/powerpoints" element={<AdminDecksPage />} />
                {/* Phase 26 Slice 2: quiet "Setup/Advanced" home for the importer (soft-hide, no role gate) */}
                <Route path="/admin/content/setup" element={<ContentSetup />} />
                <Route path="/admin/content/books/:bookId/qr-codes" element={<QRCodeList />} />
                {/* Bare hierarchy paths are not real destinations (audio→page→book,
                    QR/pages→book). Redirect bookmarks to the books workspace; the
                    ContentPicker provides the real cascade into the managers below. */}
                <Route path="/admin/content/pages" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/units" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/lessons" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/sessions" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/pages/:pageId/audio" element={<AudioManagement />} />
                <Route path="/admin/content/audio" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/pages/:pageId/videos" element={<VideoManagement />} />
                <Route path="/admin/content/videos" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/guides" element={<TeacherGuideList />} />
                <Route path="/admin/content/guides/:id/upload" element={<TeacherGuideUpload />} />
                <Route path="/admin/content/qr-codes" element={<Navigate to="/admin/content/books" replace />} />
                <Route path="/admin/content/qr-codes/:id" element={<QRCodeManagement />} />
                <Route path="/admin/tiers" element={<TierManagement />} />
              </Route>
            </Route>
          </Route>

          {/* ========================================
              TEACHER ROUTES
              Requires authentication + TEACHER role (strict)
              Uses TeacherLayout with bottom tab navigation
          ======================================== */}
          <Route element={<ProtectedRoute />}>
            <Route element={<RoleRoute roles="TEACHER" />}>
              <Route element={<TeacherLayout />}>
                {/* The ONE teacher home: greeting + continue reading + levels
                    grid -> per-level hub + other materials. */}
                <Route path="/teacher" element={<TeacherLevelsHomePage />} />
                <Route path="/teacher/levels/:levelId" element={<TeacherLevelHubPage />} />
                <Route
                  path="/teacher/levels/:levelId/powerpoints"
                  element={<TeacherLevelDecksPage />}
                />
                {/* Legacy second home — merged into /teacher (Phase 25). */}
                <Route path="/teacher/browse" element={<Navigate to="/teacher" replace />} />
                <Route path="/teacher/books" element={<BooksBrowsePage />} />
                <Route path="/teacher/books/:bookId/view" element={<BookViewerPage />} />
                <Route path="/teacher/audio" element={<AudioBrowsePage />} />
                <Route path="/teacher/audio/:bookId" element={<AudioListPage />} />
                <Route path="/teacher/videos/:levelId" element={<LevelVideosPage />} />
                <Route path="/teacher/guides" element={<TeacherGuidesBrowsePage />} />
                <Route path="/teacher/guides/:id/view" element={<TeacherGuideViewerPage />} />
                <Route path="/teacher/lesson-plans" element={<TeacherLessonPlansPage />} />
                <Route
                  path="/teacher/lesson-plans/preferences"
                  element={<TeacherLessonPlanPreferencesPage />}
                />
                <Route path="/teacher/games" element={<TeacherGamesPage />} />
                <Route path="/teacher/games/create" element={<CreateGamePage />} />
                <Route path="/teacher/games/:templateId/play" element={<GamePlayPage />} />
                <Route path="/teacher/games/:templateId/leaderboard" element={<GameLeaderboardPage />} />
                <Route path="/teacher/profile" element={<TeacherProfilePage />} />
              </Route>
            </Route>
          </Route>

          {/* ========================================
              STUDENT ROUTES
              Requires authentication + STUDENT role (strict)
              Uses StudentLayout with bottom tab navigation
          ======================================== */}
          <Route element={<ProtectedRoute />}>
            <Route element={<RoleRoute roles="STUDENT" />}>
              <Route element={<StudentLayout />}>
                <Route path="/student" element={<StudentHomePage />} />
                <Route path="/student/books" element={<BooksBrowsePage />} />
                <Route path="/student/books/:bookId/view" element={<BookViewerPage />} />
                <Route path="/student/audio" element={<AudioBrowsePage />} />
                <Route path="/student/audio/:bookId" element={<AudioListPage />} />
                <Route path="/student/videos" element={<VideosBrowsePage />} />
                <Route path="/student/games" element={<StudentGamesPage />} />
                <Route path="/student/games/history" element={<GameHistoryPage />} />
                <Route path="/student/games/:templateId/play" element={<GamePlayPage />} />
                <Route path="/student/games/:templateId/leaderboard" element={<GameLeaderboardPage />} />
                <Route path="/student/profile" element={<StudentProfilePage />} />
              </Route>
            </Route>
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </RouteErrorBoundaryWrapper>
    </Suspense>
  );
};

export default App;
